import { memo } from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { Link } from "react-router-dom";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import ArticleIcon from "@mui/icons-material/Article";

const Blog = memo(() => {
  const handleScrollToNewsletter = () => {
    const element = document.getElementById("newsletter-signup");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => {
        const nameInput = document.getElementById("newsletter-name-input");
        if (nameInput) {
          nameInput.focus();
        }
      }, 800);
    }
  };

  return (
    <Container maxWidth="xl" disableGutters>
      <FullPaddingWrapper>
        {/* Hero Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #004B4C 0%, #006668 100%)",
            borderRadius: { xs: "16px", md: "24px" },
            minHeight: { xs: 400, md: 500 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: { xs: 4, md: 8 },
            mb: { xs: 4, md: 6 },
          }}
        >
          <Box
            sx={{
              width: { xs: 80, md: 120 },
              height: { xs: 80, md: 120 },
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 4,
            }}
          >
            <ArticleIcon
              sx={{
                fontSize: { xs: 40, md: 60 },
                color: "#ffffff",
              }}
            />
          </Box>

          <Typography
            variant="h2"
            sx={{
              color: "#ffffff",
              fontWeight: 700,
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            SimplerHome Blog
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "#ffffff",
              fontSize: { xs: "1rem", md: "1.2rem" },
              fontWeight: 300,
              lineHeight: 1.6,
              maxWidth: 600,
              opacity: 0.9,
              mb: 4,
            }}
          >
            Expert tips, home maintenance guides, and insights to help you
            become a smarter, more confident homeowner.
          </Typography>

          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.15)",
              borderRadius: "12px",
              px: { xs: 4, md: 6 },
              py: { xs: 2, md: 3 },
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#ffffff",
                fontWeight: 600,
                fontSize: { xs: "1rem", md: "1.25rem" },
              }}
            >
              Coming Soon
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#ffffff",
                opacity: 0.8,
                mt: 1,
              }}
            >
              We're crafting valuable content for you. Stay tuned!
            </Typography>
          </Box>
        </Box>

        {/* Topics Preview Section */}
        <Box
          sx={{
            py: { xs: 6, md: 10 },
            px: { xs: 2, md: 4 },
            textAlign: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: "primary.main",
              fontWeight: 700,
              fontSize: { xs: "1.5rem", md: "2rem" },
              mb: 3,
            }}
          >
            What We'll Cover
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "#333",
              fontSize: { xs: "1rem", md: "1.1rem" },
              lineHeight: 1.6,
              maxWidth: 700,
              mx: "auto",
              mb: 6,
            }}
          >
            Our blog will feature expert advice and practical tips on everything
            related to homeownership and property management.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
              gap: 3,
              maxWidth: 1200,
              mx: "auto",
            }}
          >
            {[
              {
                title: "Seasonal Maintenance",
                description: "Keep your home in top shape year-round",
              },
              {
                title: "DIY Projects",
                description: "Step-by-step guides for home improvements",
              },
              {
                title: "Cost Savings",
                description: "Tips to reduce expenses and find rebates",
              },
              {
                title: "Home Value",
                description: "Investments that boost your property worth",
              },
            ].map((topic, index) => (
              <Box
                key={index}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  backgroundColor: "#f8f8f8",
                  transition: "transform 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    fontSize: { xs: "1rem", md: "1.1rem" },
                    mb: 1,
                  }}
                >
                  {topic.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    lineHeight: 1.5,
                  }}
                >
                  {topic.description}
                </Typography>
              </Box>
            ))}
          </Box>

          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={handleScrollToNewsletter}
            sx={{
              mt: 8,
              px: { xs: 5, md: 10, lg: 15 },
              py: { xs: 1.5, md: 2, lg: 2.5 },
              fontSize: { xs: "1rem", md: "1.1rem", lg: "1.2rem" },
              fontWeight: 500,
              borderRadius: "8px",
              textTransform: "none",
              color: "#ffffff",
            }}
          >
            Subscribe for Updates
          </Button>
          <Box sx={{ mt: 2 }}>
            <Button
              component={Link}
              to="/contact?topic=feedback&subject=Blog%20topic%20idea"
              variant="text"
              sx={{ textTransform: "none", fontSize: "1rem" }}
            >
              Suggest a topic you'd like us to cover
            </Button>
          </Box>
        </Box>
      </FullPaddingWrapper>
    </Container>
  );
});

export default Blog;
